import { PredictorColors, SpecialQFColors } from "../color-themes/color-themes";
export interface Icon {
  icon?: string;
  color: string;
}

export enum FieldSpec {
  PhraseClusters = "phrase_clusters",
  Topic = "topic",
  Intent = "intent",
  Emotion = "emotion",
  Categories = "categories",
  Segment = "segment",
  EmitterRole = "emitter_role",
  Sentiment = "sentiment",
  ElapsedTime = "elapsed_time",
  MsgTime = "msg_time",
  MsgDuration = "msg_duration",
  Emitter = "emitter",
  MsgDate = "msg_date",
  MsgDateTime = "msg_date_time",
  DateTimeAlt = "date/time",
  Uuid = "uuid",
  VerbatimId = "verbatim_id",
  MsgSequence = "msg_sequence",
  MsgCount = "msg_count",
  SessionDuration = "session_duration",
  MsgCharLength = "msg_char_length",
  VerbatimName = "verbatim_name",
  OriginalVerbatim = "original_verbatim",
  Keyword = "keyword",
  Text = "text",
  Date = "date",
  Long = "long",
  Number = "number",
  Float = "float",
  HHMMSS = "HH:mm:ss",
  Boolean = "boolean",
  White = "white",
}

export function sentimentIcon(sentiment: number): Icon {
  if (sentiment <= -1) {
    return { icon: "TablerMoodSad", color: "red" };
  }
  if (sentiment > -1 && sentiment < 1) {
    return { icon: "TablerMoodNeutral", color: "gray" };
  }
  return { icon: "TablerMoodHappy", color: "green" };
}

const effort: Icon = {
  icon: "TablerFlame",
  color: SpecialQFColors.Effort,
};

const timeStyles: Icon = {
  icon: "HeroClock",
  color: "var(--fds-color-grey-600)",
};

const dateStyles: Icon = {
  icon: "TablerCalendar",
  color: "var(--fds-color-grey-600)",
};

const uuidStyles: Icon = {
  icon: "HeroFingerPrint",
  color: "var(--fds-color-grey-600)",
};

const numberStyles: Icon = {
  icon: "TablerHash",
  color: "var(--fds-color-grey-600)",
};

const long: Icon = {
  icon: "TablerListNumbers",
  color: "var(--fds-color-grey-600)",
};

const msgSequence: Icon = {
  icon: "TablerMessages",
  color: "var(--fds-color-grey-600)",
};

const msgLength: Icon = {
  icon: "TablerFileText",
  color: "var(--fds-color-grey-600)",
};

const textIcon: Icon = {
  icon: "TablerFileText",
  color: "var(--fds-color-grey-600)",
};

export type FieldIconMap = Partial<Record<FieldSpec, Icon>> & {
  effort?: Icon;
  cumulative_effort?: Icon;
  fluff?: Icon;
};

// Canonical mapping of field spec → icon.
export const fieldIcon: FieldIconMap = {
  [FieldSpec.PhraseClusters]: { icon: "TablerChartCircles", color: "var(--fds-color-grey-600)" },
  [FieldSpec.Topic]: { icon: "HeroNewspaper", color: PredictorColors.Topics },
  [FieldSpec.Intent]: { icon: "TablerTarget", color: PredictorColors.Intents },
  [FieldSpec.Emotion]: {
    icon: "TablerMoodHappy",
    color: PredictorColors.Emotions,
  },
  [FieldSpec.Categories]: {
    icon: "TablerLayoutGrid",
    color: PredictorColors.Categories,
  },
  [FieldSpec.Segment]: { icon: "TablerLayoutBoardSplit", color: "var(--fds-color-grey-600)" },
  [FieldSpec.EmitterRole]: { icon: "TablerUserCircle", color: "var(--fds-color-grey-600)" },
  effort,
  cumulative_effort: effort, // legacy alias keys outside enum
  fluff: { icon: "TablerCandy", color: PredictorColors.Fluff },
  [FieldSpec.Sentiment]: {
    icon: "TablerMoodSmile",
    color: SpecialQFColors.Sentiment,
  },

  [FieldSpec.HHMMSS]: timeStyles,
  [FieldSpec.ElapsedTime]: timeStyles,
  [FieldSpec.MsgDuration]: timeStyles,
  [FieldSpec.MsgTime]: { icon: "TablerClock", color: "var(--fds-color-grey-600)" },
  [FieldSpec.Emitter]: { icon: "TablerUser", color: "var(--fds-color-grey-600)" },

  [FieldSpec.Date]: dateStyles,
  [FieldSpec.MsgDate]: dateStyles,
  [FieldSpec.SessionDuration]: dateStyles,
  [FieldSpec.MsgDateTime]: { icon: "TablerCalendarTime", color: "var(--fds-color-grey-600)" },
  [FieldSpec.DateTimeAlt]: { icon: "TablerCalendarTime", color: "var(--fds-color-grey-600)" },

  [FieldSpec.Uuid]: uuidStyles,
  [FieldSpec.VerbatimId]: uuidStyles,

  [FieldSpec.MsgSequence]: msgSequence,
  [FieldSpec.MsgCount]: numberStyles,
  [FieldSpec.MsgCharLength]: msgLength,

  [FieldSpec.VerbatimName]: textIcon,
  [FieldSpec.OriginalVerbatim]: textIcon,
  [FieldSpec.Text]: textIcon,

  [FieldSpec.Keyword]: { icon: "TablerTag", color: "var(--fds-color-grey-600)" },

  [FieldSpec.Long]: long,
  [FieldSpec.Number]: long,
  [FieldSpec.Float]: long,

  [FieldSpec.Boolean]: { icon: "TablerListCheck", color: "var(--fds-color-grey-600)" },
  [FieldSpec.White]: { color: "var(--fds-color-white-0)" },
} as FieldIconMap;

export type FieldIcon = FieldIconMap;

const FALLBACK_ICON: Icon = { icon: "TablerLayoutGrid", color: "var(--fds-color-grey-600)" };

type FieldLike = {
  path?: unknown;
  type?: unknown;
  name?: unknown;
};

type FieldParts = {
  path: string;
  type: string;
  name: string;
};

const asStringOrEmpty = (value: unknown): string =>
  typeof value === "string" ? value : "";

const getFieldParts = (field: FieldLike): FieldParts => ({
  path: asStringOrEmpty(field.path),
  type: asStringOrEmpty(field.type),
  name: asStringOrEmpty(field.name),
});

type SpecDeriver = (parts: FieldParts) => string;

const DEFAULT_SPEC_DERIVER: SpecDeriver = ({ type, name }) => type || name;

const SPEC_DERIVERS: Readonly<Record<string, SpecDeriver>> = {
  Models: ({ path, name }) => path || name,
  "Calculated Metrics": ({ path, name }) => path || name,
  "Chat Transcript Fields": ({ name }) => name,
  NLP: ({ path, type }) => (path === FieldSpec.PhraseClusters ? path : type),
  "Structured Fields": ({ path, type }) =>
    path === FieldSpec.PhraseClusters ? path : type,
};

const getIconForSpec = (spec: string): Icon => {
  const iconMap = fieldIcon as unknown as Record<string, Icon | undefined>;
  return iconMap[spec] || FALLBACK_ICON;
};

export function setFieldIcon(groupType: string, field: any): Icon | undefined {
  if (!field) return FALLBACK_ICON;

  const parts = getFieldParts(field as FieldLike);
  const deriveSpec = SPEC_DERIVERS[groupType] || DEFAULT_SPEC_DERIVER;
  const spec = deriveSpec(parts);

  return getIconForSpec(spec);
}
